import { Link } from 'react-router-dom';
import { ArrowRight, Compass, Leaf, Scale, Sparkles, Users, Heart, Globe, Lightbulb, Calendar, Clock, ChevronRight } from 'lucide-react';
import { services, values, testimonials } from '../data/values';
import { offerings } from '../data/courses';
import { getRecentPosts } from '../data/blog';
import { getNextEvent } from '../data/events';

const HERO_IMAGE = 'https://customer-assets.emergentagent.com/job_stem-career-hub/artifacts/0e57a8qe_jrdesignphotograpy_-9156.jpg';

const iconMap = {
  Compass, Leaf, Scale, Sparkles, Users, Heart, Globe, Lightbulb
};

const Home = () => {
  const recentPosts = getRecentPosts(3);
  const nextEvent = getNextEvent();

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-white via-gray-50 to-brand-turquoise/5" data-testid="hero-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Left Content */}
            <div className="animate-fade-in">
              <span className="badge-lime inline-block mb-6">STEAM Career Guidance</span>
              <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-brand-blue leading-tight mb-6">
                Your Path to a{' '}
                <span className="gradient-text">Limitless Future</span>
              </h1>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed max-w-xl">
                STEMJECTORY guides students and professionals in STEAM fields to design 
                non-linear careers with purpose, wellness, and balance. Your career 
                doesn't have to be a straight line.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/consulting" className="btn-primary inline-flex items-center justify-center gap-2" data-testid="cta-explore-mentorship">
                  Explore Mentorship
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link to="/courses" className="btn-outline inline-flex items-center justify-center gap-2" data-testid="cta-discover-resources">
                  Discover Resources
                </Link>
              </div>
            </div>

            {/* Right - Hero Image */}
            <div className="relative animate-slide-in" style={{ animationDelay: '200ms' }}>
              <div className="hero-blob aspect-square max-w-lg mx-auto overflow-hidden shadow-2xl">
                <img
                  src={HERO_IMAGE}
                  alt="Diana - Founder of STEMJECTORY (placeholder)"
                  className="w-full h-full object-cover"
                  data-testid="hero-diana-image"
                />
              </div>
              {/* Decorative Elements */}
              <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-brand-lime rounded-2xl -z-10 opacity-60"></div>
              <div className="absolute -top-4 -right-4 w-32 h-32 bg-brand-turquoise rounded-full -z-10 opacity-40"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Three Pillars / Services Section */}
      <section className="section-padding bg-white" data-testid="services-section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-brand-blue mb-4">
              How We Guide Your Journey
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Three interconnected pillars that form the foundation of a fulfilling STEAM career.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const Icon = iconMap[service.icon];
              return (
                <div
                  key={service.id}
                  className="card-marketing card-hover group"
                  style={{ animationDelay: `${index * 100}ms` }}
                  data-testid={`service-card-${service.id}`}
                >
                  <div className="w-14 h-14 rounded-xl bg-brand-turquoise/10 flex items-center justify-center mb-6 group-hover:bg-brand-turquoise/20 transition-colors">
                    <Icon className="w-7 h-7 text-brand-turquoise" />
                  </div>
                  <h3 className="font-heading font-bold text-xl text-brand-blue mb-3">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 mb-4 leading-relaxed">
                    {service.description}
                  </p>
                  <Link
                    to={service.link}
                    className="inline-flex items-center text-brand-magenta font-medium hover:gap-2 transition-all"
                  >
                    Learn more <ChevronRight className="w-4 h-4 ml-1" />
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section-padding bg-gray-50" data-testid="values-section">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-brand-blue mb-4">
              Our Core Values
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              The principles that guide everything we do at STEMJECTORY.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
            {values.map((value) => {
              const Icon = iconMap[value.icon];
              return (
                <div
                  key={value.id}
                  className="bg-white rounded-xl p-6 text-center card-hover border border-gray-100"
                  data-testid={`value-card-${value.id}`}
                >
                  <div className="w-12 h-12 rounded-full bg-brand-lime/20 flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-6 h-6 text-brand-blue" />
                  </div>
                  <h4 className="font-heading font-bold text-brand-blue mb-2">{value.title}</h4>
                  <p className="text-sm text-gray-500">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Offerings Section */}
      <section className="section-padding bg-white" data-testid="offerings-section">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-12 gap-4">
            <div>
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-brand-blue mb-2">
                Resources to Get Started
              </h2>
              <p className="text-gray-600">Practical tools to kickstart your career transformation.</p>
            </div>
            <Link to="/courses" className="btn-secondary inline-flex items-center gap-2">
              View All <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {offerings.map((offering) => (
              <div
                key={offering.id}
                className="bg-white rounded-2xl overflow-hidden border border-gray-100 card-hover"
                data-testid={`offering-card-${offering.id}`}
              >
                <div className="aspect-video overflow-hidden">
                  <img
                    src={offering.image}
                    alt={offering.title}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <span className="badge-lime text-xs mb-3 inline-block">{offering.type}</span>
                  <h3 className="font-heading font-bold text-lg text-brand-blue mb-2">
                    {offering.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">{offering.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="font-accent font-bold text-xl text-brand-blue">{offering.price}</span>
                    <button className="btn-primary text-sm py-2 px-4" data-testid={`checkout-btn-${offering.id}`}>
                      Checkout coming soon
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="section-padding bg-brand-blue" data-testid="testimonials-section">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-4">
              What Our Community Says
            </h2>
            <p className="text-gray-300 max-w-2xl mx-auto">
              Real stories from professionals who've transformed their careers.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <div
                key={testimonial.id}
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 relative"
                data-testid={`testimonial-card-${testimonial.id}`}
              >
                <div className="testimonial-quote relative">
                  <p className="text-white/90 leading-relaxed mb-6 italic">
                    "{testimonial.quote}"
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <p className="font-bold text-white">{testimonial.name}</p>
                    <p className="text-sm text-brand-turquoise">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Preview Section */}
      <section className="section-padding bg-gray-50" data-testid="blog-preview-section">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-12 gap-4">
            <div>
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-brand-blue mb-2">
                Latest Insights
              </h2>
              <p className="text-gray-600">Thoughts on careers, wellness, and thriving in STEAM.</p>
            </div>
            <Link to="/blog" className="btn-outline inline-flex items-center gap-2">
              Read All Posts <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {recentPosts.map((post) => (
              <Link
                key={post.id}
                to={`/blog/${post.slug}`}
                className="group"
                data-testid={`blog-preview-${post.id}`}
              >
                <div className="bg-white rounded-2xl overflow-hidden border border-gray-100 card-hover">
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="badge-lime text-xs">{post.category}</span>
                      <span className="text-sm text-gray-400">{post.readTime}</span>
                    </div>
                    <h3 className="font-heading font-bold text-lg text-brand-blue mb-2 group-hover:text-brand-turquoise transition-colors">
                      {post.title}
                    </h3>
                    <p className="text-gray-600 text-sm line-clamp-2">{post.excerpt}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-white" data-testid="cta-section">
        <div className="container-custom">
          <div className="bg-gradient-to-r from-brand-turquoise to-brand-lime rounded-3xl p-12 md:p-16 text-center relative overflow-hidden">
            <div className="relative z-10">
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-brand-blue mb-4">
                Ready to Map Your Trajectory?
              </h2>
              <p className="text-brand-blue/80 mb-8 max-w-xl mx-auto">
                Join our community of STEAM professionals who are designing careers on their own terms.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/consulting" className="btn-primary bg-brand-blue hover:bg-brand-blue/90" data-testid="cta-bottom-consulting">
                  Book a Consultation
                </Link>
                <Link to="/courses" className="btn-outline border-brand-blue text-brand-blue hover:bg-brand-blue hover:text-white">
                  Explore Courses
                </Link>
              </div>
            </div>
            {/* Decorative circles */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/20 rounded-full -translate-y-1/2 translate-x-1/2"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/20 rounded-full translate-y-1/2 -translate-x-1/2"></div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
